import React, { useState } from "react";
import { useDispatch } from "react-redux";
import log from "loglevel";
import { Button, Modal } from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addAccountUserAction,
  deleteAccountUserAction,
} from "store/actions/actionCreators";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import {
  Account,
  AccountUser,
  User,
  Role,
  AddAccountUserRequestType,
  DeleteAccountUserRequestType,
} from "types";
import {
  generateAccountDropdownItems,
  generateUserDropdownItems,
  generateRoleDropdownItems,
} from "util/dashboardUtil";
import AccountUsersTable from "./AccountUsersTable";
import genStyles from "styles/General.module.scss";

type AddAccountUserFormType = {
  user_id: number | undefined;
  account_id: number | undefined;
  role_id: number | undefined;
  username?: string;
  account_name?: string;
  role_name?: string;
};

const defaultAccountUser: AddAccountUserFormType = {
  user_id: undefined,
  account_id: undefined,
  role_id: undefined,
};

const AccountUsersCard = ({
  accountUsers,
  accounts,
  users,
  roles,
  showCreateModal,
  setShowCreateModal,
  showSearch,
}: {
  accountUsers: AccountUser[];
  accounts: Account[];
  users: User[];
  roles: Role[];
  showCreateModal: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSearch: boolean;
}) => {
  const dispatch = useDispatch();

  const [selectedAccountUser, setSelectedAccountUser] = useState<AccountUser>();
  const [addAccountUser, setAddAccountUser] = useState<AddAccountUserFormType>(
    defaultAccountUser
  );
  // eslint-disable-next-line
  const [isAddingAccountUser, setIsAddingAccountUser] = useState(false);
  // eslint-disable-next-line
  const [isDeletingAccountUser, setIsDeletingAccountUser] = useState(false);
  // NOTE: Moved to outside component
  //   const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const validateAddAccountUser = (
    addForm: AddAccountUserFormType | undefined
  ) => {
    return (
      addForm && addForm?.user_id && addForm?.account_id && addForm?.role_id
    );
  };

  const onHandleAddAccountUserClick = (event: any) => {
    event.preventDefault();
    log.info("Attempting to ADD AccountUser...");
    if (
      addAccountUser?.user_id &&
      addAccountUser?.account_id &&
      addAccountUser?.role_id
    ) {
      const request: AddAccountUserRequestType = {
        body: {
          user_id: addAccountUser.user_id,
          account_id: addAccountUser.account_id,
          role_id: addAccountUser.role_id,
        },
      };
      log.info("-- Dispatching addAccountUserAction()...");
      dispatch(addAccountUserAction(request));
      setIsAddingAccountUser(false);
      setShowCreateModal(false);
      setAddAccountUser(defaultAccountUser); // clear
    } else {
      log.info("One or more fields NOT VALID!");
      log.info("AddAccountUserFormType: " + JSON.stringify(addAccountUser));
    }
  };

  const onHandleRemoveAccountUserClick = (event: any) => {
    event.preventDefault();
    if (selectedAccountUser) {
      const request: DeleteAccountUserRequestType = {
        id: selectedAccountUser.id,
      };
      log.info("-- Dispatching deleteAccountUserAction()...");
      dispatch(deleteAccountUserAction(request));
      setIsDeletingAccountUser(false);
      setShowDeleteModal(false);
      setSelectedAccountUser(undefined); // clear
    }
  };

  const accountUserTableRowEvents = {
    onClick: (e: any, row: AccountUser, rowIndex: number) => {
      log.info("Row Index: " + rowIndex);
      log.info("Row: " + JSON.stringify(row));
      setSelectedAccountUser(row);
      setShowDeleteModal(true);
    },
  };

  const onSelectAccountFromDropdown = (accountItem: any) => {
    if (accountItem) {
      log.info(
        "Add AccountUser: Account Dropdown Item, Selected! : " +
          JSON.stringify(accountItem)
      );
      setAddAccountUser((state): typeof state => ({
        ...state,
        account_id: accountItem.id,
        account_name: accountItem.label,
      }));
    }
  };

  const onSelectUserFromDropdown = (userItem: any) => {
    if (userItem) {
      log.info(
        "Add AccountUser: User Dropdown Item, Selected! : " +
          JSON.stringify(userItem)
      );
      setAddAccountUser((state): typeof state => ({
        ...state,
        user_id: userItem.id,
        username: userItem.label,
      }));
    }
  };

  const onSelectRoleFromDropdown = (roleItem: any) => {
    if (roleItem) {
      log.info(
        "Add AccountUser: Role Dropdown Item, Selected! : " +
          JSON.stringify(roleItem)
      );
      setAddAccountUser((state): typeof state => ({
        ...state,
        role_id: roleItem.id,
        role_name: roleItem.label,
      }));
    }
  };

  return (
    <>
      <AccountUsersTable
        accountUsers={accountUsers}
        rowEvents={accountUserTableRowEvents}
        showSearch={showSearch}
        showAccount={true}
        showRole={true}
      />
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setAddAccountUser(defaultAccountUser);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Add User to Account{" "}
              <FontAwesomeIcon icon={["fal", "users-class"]} />
            </Modal.Title>
          </Modal.Header>
          <form name="create-analysis-form">
            <Modal.Body>
              <div>
                <div className="form-group">
                  <Select
                    options={generateAccountDropdownItems(accounts)}
                    placeholder={"Select Account..."}
                    isClearable={false}
                    onChange={onSelectAccountFromDropdown}
                  />
                </div>
                <div className="form-group">
                  <Select
                    options={generateUserDropdownItems(users)}
                    placeholder={"Select User..."}
                    isClearable={false}
                    onChange={onSelectUserFromDropdown}
                  />
                </div>
                <div className="form-group">
                  <Select
                    options={generateRoleDropdownItems(roles)}
                    placeholder={"Select Role..."}
                    isClearable={false}
                    onChange={onSelectRoleFromDropdown}
                  />
                </div>
              </div>
              <div>
                {validateAddAccountUser(addAccountUser) ? (
                  <p style={{ fontStyle: "italic" }}>
                    {`This will add the user "${addAccountUser.username}" 
                  to the account "${addAccountUser.account_name}" 
                  with the role of "Account ${addAccountUser.role_name}"`}
                  </p>
                ) : null}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <input
                type="submit"
                value="Add"
                className={[
                  genStyles["alenza__btn-create__mini"],
                  "float-right",
                ].join(" ")}
                disabled={!validateAddAccountUser(addAccountUser)}
                onClick={(e: any) => {
                  log.info("Add AccountUser Button Clicked!");
                  setIsAddingAccountUser(true);
                  onHandleAddAccountUserClick(e);
                }}
              />
            </Modal.Footer>
          </form>
        </Modal>
      </>
      {/* AccountUser Settings Modal */}
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedAccountUser(undefined);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Remove User from Account{" "}
              <FontAwesomeIcon icon={["fal", "user-times"]} />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedAccountUser ? (
              <>
                <form name="delete-form">
                  <p>
                    Removing user "
                    <span className="font-bold">
                      {`${selectedAccountUser.username} (${selectedAccountUser.email})`}
                    </span>
                    " from account "
                    <span className="font-bold">
                      {selectedAccountUser.account_name}
                    </span>
                    ".
                  </p>
                  <p>Are you sure you want to remove this user?</p>
                </form>
                <input
                  type="submit"
                  value="Remove"
                  className={[
                    genStyles["alenza__btn-delete__mini"],
                    "float-right",
                  ].join(" ")}
                  onClick={(e: any) => {
                    setIsDeletingAccountUser(true);
                    onHandleRemoveAccountUserClick(e);
                  }}
                />
              </>
            ) : (
              <p>
                Unable to remove user from this account at this time. Please try
                again later.
              </p>
            )}
          </Modal.Body>
        </Modal>
      </>
    </>
  );
};

export default AccountUsersCard;
